import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { keycloakSession } from "@/auth/keycloak-session";

export type AuthenticationStatus =
  "idle" | "initializing" | "anonymous" | "authenticated" | "error";

export const useAuthStore = defineStore("auth", () => {
  const status = ref<AuthenticationStatus>("idle");
  const errorMessage = ref<string | null>(null);
  let initialization: Promise<void> | null = null;

  keycloakSession.subscribe((authenticated) => {
    status.value = authenticated ? "authenticated" : "anonymous";
    if (authenticated) {
      errorMessage.value = null;
    }
  });

  const isAuthenticated = computed(() => status.value === "authenticated");
  const isInitializing = computed(() => status.value === "idle" || status.value === "initializing");

  function initialize(): Promise<void> {
    if (initialization === null) {
      status.value = "initializing";
      initialization = keycloakSession
        .initialize()
        .then((authenticated) => {
          status.value = authenticated ? "authenticated" : "anonymous";
        })
        .catch(() => {
          status.value = "error";
          errorMessage.value = "Sign-in is temporarily unavailable.";
        });
    }
    return initialization;
  }

  async function login(returnPath = "/dashboard"): Promise<void> {
    errorMessage.value = null;
    try {
      await keycloakSession.login(returnPath);
    } catch {
      status.value = "error";
      errorMessage.value = "Sign-in is temporarily unavailable.";
    }
  }

  async function register(returnPath = "/account"): Promise<void> {
    errorMessage.value = null;
    try {
      await keycloakSession.register(returnPath);
    } catch {
      status.value = "error";
      errorMessage.value = "Account creation is temporarily unavailable.";
    }
  }

  async function logout(): Promise<void> {
    status.value = "anonymous";
    errorMessage.value = null;
    try {
      await keycloakSession.logout();
    } catch {
      errorMessage.value =
        "You are signed out locally, but Washbox could not reach the identity provider.";
    }
  }

  return {
    status,
    errorMessage,
    isAuthenticated,
    isInitializing,
    initialize,
    login,
    register,
    logout,
  };
});
